import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import { ModelRuleData } from '../../modelConfiguration/rules/modelRuleData';
import { ModelTreeNode } from '../../modelConfiguration/data/modelOptionsTree';
import { TreeSelections } from '../../modelConfiguration/data/treeSelection';
import { TreeOptionSelections } from '../../modelConfiguration/data/optionsSelection';
import { TreeBuilding } from '../../modelConfiguration/logic/treeBuilding';
import { TreeOptionsSelected } from '../../modelConfiguration/logic/treeOptionsSelected';
import { SLTXFormatRootFile } from '../../fileFormats/sltx/sltx';
import { ModelConfigurationLinearRoot } from './modelConfigurationLinearRoot';
import { CatalogTagsTranslation } from '../../modelViewer/Data/catalogTagsTranslation';

interface ModelConfigurationLinearProps {
  sltxFile: SLTXFormatRootFile | undefined;
  rules: ModelRuleData[];
  optionsSelectionState : TreeOptionSelections,
  nodeIndex : number,
  tagsTranslation : CatalogTagsTranslation[],
  languageSelected : string,
  updateOptionsSelectionState : (selection : TreeOptionSelections) => void,
  getResourcePath : (path : string) => string,
}

export function ModelConfigurationLinear({
  sltxFile,
  rules,
  optionsSelectionState,
  nodeIndex,
  tagsTranslation,
  languageSelected,
  updateOptionsSelectionState,
  getResourcePath,
} : ModelConfigurationLinearProps) : JSX.Element {

  // Tree to render - It will differ from the parsed tree when rules are applied
  // const [optionsList, setOptionsList] = useState<ModelTreeNode[]>();
  const [treeToRender, setTreeToRender] = useState<ModelTreeNode>();

  // Tree selection state - This should contain all the selections in the tree
  // and the last updated value
  const [
    treeSelectionState,
    setTreeSelectionState,
  ] = useState<TreeSelections>([]);

  const updateOptionSelection = (groupID : number, optionID : number | undefined) => {

    // Create a copy of the selection variables
    // We will update them once at the end, to avoid unnecessary updates and
    // have more control over the process
    if (!sltxFile) return;

    const tempTreeSelectionState = _.cloneDeep(treeSelectionState);
    let tempOptionsSelectionState = _.cloneDeep(optionsSelectionState);

    // Update the temp tree selection state
    tempOptionsSelectionState = tempOptionsSelectionState.filter(
      (selection) => selection.groupID !== groupID,
    );

    if (optionID !== undefined && !Number.isNaN(optionID) && !Number.isNaN(groupID)) {
      // Add the new entry
      tempOptionsSelectionState.push({
        groupID,
        optionID,
      });
    }

    let safetyLoopCount = 100;
    let tree : ModelTreeNode | undefined;

    while (safetyLoopCount > 0) {
      safetyLoopCount -= 1;

      // Build a tree that considers the new node selection
      tree = TreeBuilding.buildTree(
        rules,
        sltxFile,
        tempOptionsSelectionState,
      );

      if (tree === undefined) {
        return;
      }

      // Ensure the user selection is still valid and fix it if necessary
      let changesDone = TreeOptionsSelected.ensureUserSelectionIsValid(
        tree,
        _.cloneDeep(tempOptionsSelectionState),
        _.cloneDeep(tempTreeSelectionState),
        sltxFile,
        (newGroupID, newOptionID) => {

          // Remove the previous entry with the same group ID
          const newOptionsSelection = tempOptionsSelectionState.filter(
            (selection) => selection.groupID !== newGroupID,
          );

          // Add the new entry
          if (newOptionID !== undefined) {
            newOptionsSelection.push({ groupID: newGroupID, optionID: newOptionID });
          }

          // Copy the items from newOptionsSelection to tempOptionsSelected
          tempOptionsSelectionState.splice(0, Infinity, ...newOptionsSelection);
        },
      );

      if (TreeOptionsSelected.applyOptionsSelectionsToTreeSelections(
        tempOptionsSelectionState,
        tempTreeSelectionState,
        tree,
        (newTreeSelectionState) => {
          // Replace the contents of the local variable
          tempTreeSelectionState.splice(0, Infinity, ...newTreeSelectionState);
        },
      )) {

        changesDone = true;
      }

      if (changesDone === false) {
        break;
      }
    }

    if (tree === undefined) {
      return;
    }

    // Now, just update all the state variables
    setTreeSelectionState(tempTreeSelectionState);
    updateOptionsSelectionState(tempOptionsSelectionState);
    setTreeToRender(tree);
  };

  const updateTreeSelection = (newSelection : TreeSelections) => {

    const tempTreeSelectionState = _.cloneDeep(newSelection);
    const tempOptionsSelectionState = _.cloneDeep(optionsSelectionState);

    if (!sltxFile) return;

    let safetyLoopCount = 100;
    let tree : ModelTreeNode | undefined;
    while (safetyLoopCount > 0) {
      safetyLoopCount -= 1;

      // Build a tree that considers the new node selection
      tree = TreeBuilding.buildTree(
        rules,
        sltxFile,
        tempOptionsSelectionState,
      );

      if (tree === undefined) {
        return;
      }

      let changesDone = TreeOptionsSelected.ensureUserSelectionIsValid(
        tree,
        _.cloneDeep(tempOptionsSelectionState),
        _.cloneDeep(tempTreeSelectionState),
        sltxFile,
        (groupID, optionID) => {

          // Remove the previous entry with the same group ID
          const newOptionsSelection = tempOptionsSelectionState.filter(
            (selection) => selection.groupID !== groupID,
          );

          // Add the new entry
          if (optionID !== undefined) {
            newOptionsSelection.push({ groupID, optionID });
          }

          // Copy the items from newOptionsSelection to tempOptionsSelectionState
          tempOptionsSelectionState.splice(0, Infinity, ...newOptionsSelection);
        },
      );

      if (TreeOptionsSelected.applyOptionsSelectionsToTreeSelections(
        tempOptionsSelectionState,
        tempTreeSelectionState,
        tree,
        (newTreeSelectionState) => {
          // Replace the contents of the local variable
          tempTreeSelectionState.splice(0, Infinity, ...newTreeSelectionState);
        },
      )) {
        changesDone = true;
      }

      if (changesDone === false) {
        break;
      }
    }

    // TODO PMC: If changeDone === false why recreate the tree?
    // // Build a new tree to display
    // const tree = TreeBuilding.buildTree(
    //   rules,
    //   sltxFile,
    //   tempOptionsSelectionState,
    // );

    if (tree === undefined) {
      return;
    }

    // Now, just update all the state variables
    setTreeSelectionState(tempTreeSelectionState);
    updateOptionsSelectionState(tempOptionsSelectionState);
    setTreeToRender(tree);
  };

  const createInitialSelection = () => {

    // Create a copy of the selection variables
    // We will update them once at the end, to avoid unnecessary updates and
    // have more control over the process

    if (!sltxFile) return;

    const tempTreeSelectionState = _.cloneDeep(treeSelectionState);
    const tempOptionsSelectionState = _.cloneDeep(optionsSelectionState);

    let safetyLoopCount = 100;
    while (safetyLoopCount > 0) {
      safetyLoopCount -= 1;

      // Build a temporary tree
      const temporaryTree = TreeBuilding.buildTree(
        rules,
        sltxFile,
        tempOptionsSelectionState,
      );

      if (temporaryTree === undefined) {
        break;
      }

      // Call the function that will add the default selection, if needed
      // The return indicates if something was changed

      let changesMade = TreeOptionsSelected.addDefaultSelectionIfNeeded(
        temporaryTree,
        temporaryTree,
        _.cloneDeep(tempOptionsSelectionState),
        _.cloneDeep(tempTreeSelectionState),
        (groupID, optionID) => {

          // Remove the previous entry with the same group ID
          const newOptionsSelection = tempOptionsSelectionState.filter(
            (selection) => selection.groupID !== groupID,
          );

          // Add the new entry
          if (optionID !== undefined) {
            newOptionsSelection.push({ groupID, optionID });
          }

          // Copy the items from newOptionsSelection to tempOptionsSelectionState
          tempOptionsSelectionState.splice(0, Infinity, ...newOptionsSelection);
        },
        (newTreeSelectionState) => {
          // Copy the items from newTreeSelectionState to tempTreeSelectionState
          tempTreeSelectionState.splice(0, Infinity, ...newTreeSelectionState);
        },
      );

      // Make sure the options are reflect in the tree selection
      if (
        TreeOptionsSelected.applyOptionsSelectionsToTreeSelections(
          tempOptionsSelectionState,
          tempTreeSelectionState,
          temporaryTree,
          (newTreeSelectionState) => {
            // Copy the items from newTreeSelectionState to tempTreeSelectionState
            tempTreeSelectionState.splice(0, Infinity, ...newTreeSelectionState);
          },
        )) {
        changesMade = true;
      }

      // If nothing have changed, we can stop the loop
      if (changesMade === false) {
        break;
      }
    }

    const tree = TreeBuilding.buildTree(
      rules,
      sltxFile,
      tempOptionsSelectionState,
    );

    // Update the state variables
    setTreeSelectionState(tempTreeSelectionState);
    updateOptionsSelectionState(tempOptionsSelectionState);
    setTreeToRender(tree);
  };

  useEffect(() => {
    setTreeSelectionState([]);
    createInitialSelection();

  }, [sltxFile, rules]);

  useEffect(() => {

    if (treeToRender) {
      // When the selection changes, update the tree state
      const tempTreeSelectionState = _.cloneDeep(treeSelectionState);

      TreeOptionsSelected.applyOptionsSelectionsToTreeSelections(
        optionsSelectionState,
        tempTreeSelectionState,
        treeToRender,
        (newTreeSelectionState) => {
          // Replace the contents of the local variable
          tempTreeSelectionState.splice(0, Infinity, ...newTreeSelectionState);
        },
      );

      setTreeSelectionState(tempTreeSelectionState);
    }

  }, [optionsSelectionState]);

  return (
    <div
      className="model-tree container"
      style={{ marginTop: '20px' }}
    >
      {
        treeToRender
        && treeToRender.children[nodeIndex] && (
          <ModelConfigurationLinearRoot
            node={treeToRender.children[nodeIndex]}
            treeSelectionState={treeSelectionState}
            tagsTranslation={tagsTranslation}
            languageSelected={languageSelected}
            updateTreeSelectionState={updateTreeSelection}
            updateOptionSelection={updateOptionSelection}
            getResourcePath={getResourcePath}
          />
        )
      }
    </div>
  );
}
